<template>
  <div class="w1200 data-update-page">
    <div class="page-bar">
      <span class="back-btn" @click="goBack">← 返回首页</span>
    </div>
    <div class="page-title">开奖数据更新</div>
    <div class="tip-line">
      上传你彩票软件导出的开奖数据文件，系统会自动识别格式、解析开奖号并转换为<b>标准格式</b>，直接生成一条「更新记录」，在下方列表点「下载」即可获取。
      计费规则：<b>免费版每月 3 次、单次 ≤{{ priceInfo ? priceInfo.fileFreeBatchMax : 100 }} 期</b>；超出可购买<b>单次转换（{{ fmtPrice(priceInfo && priceInfo.singlePrice, 2) }} 元/次，不限期数）</b>，或开通<b>会员（{{ fmtPrice(priceInfo && priceInfo.monthPrice, 9.9) }} 元/月、{{ fmtPrice(priceInfo && priceInfo.yearPrice, 68) }} 元/年，不限次）</b>。
    </div>

    <!-- 订阅状态 -->
    <el-card class="block" shadow="never">
      <div slot="header" class="card-head">
        <span>我的订阅</span>
        <div>
          <el-button type="primary" size="mini" @click="openPay()">开通会员 / 购买次数</el-button>
        </div>
      </div>
      <div v-loading="subLoading" class="sub-box">
        <template v-if="subInfo && subInfo.standard">
          <el-tag type="success">会员版</el-tag>
          <span class="sub-item">有效期至：{{ fmtDate(subInfo.endTime) }}</span>
          <span class="sub-item">数据转换：不限次</span>
        </template>
        <template v-else>
          <el-tag>免费版</el-tag>
          <span class="sub-item">本月转换：{{ subInfo && subInfo.fileMonthUsed !== undefined ? subInfo.fileMonthUsed : (subInfo ? subInfo.monthUsed : 0) }} / {{ subInfo && subInfo.fileMonthLimit !== undefined ? subInfo.fileMonthLimit : (subInfo ? subInfo.monthLimit : 3) }} 次</span>
          <span class="sub-item">本月剩余：{{ subInfo && subInfo.fileRemaining !== undefined ? subInfo.fileRemaining : (subInfo ? subInfo.remaining : 3) }} 次</span>
          <span class="sub-item">单次转换余量：{{ subInfo ? (subInfo.singleCreditRemain || 0) : 0 }} 次</span>
        </template>
      </div>
    </el-card>

    <!-- 我的软件数据文件 -->
    <el-card class="block" shadow="never">
      <div slot="header" class="card-head"><span>我的软件数据文件（上传后可一键追加更新）</span></div>
      <div class="upload-box">
        <div class="upload-tip">
          把你软件（大赢家/幸运500/定制软件等）导出的开奖数据文件上传到这里，系统会自动识别格式、解析开奖号，
          并直接生成一条「更新记录」（官方数据为主、补齐你录入的期号）。如需在官方新开奖后刷新，可点击「重新生成」再建一条记录。
          最终在下方「更新记录」列表中点击「下载」获取处理好的规范 txt，导入软件即完成更新。
        </div>
        <el-form inline class="upload-form">
          <el-form-item label="彩种">
            <el-select v-model="uploadForm.preType" placeholder="请选择彩种" style="width:160px">
              <el-option v-for="p in products" :key="p.preType" :label="p.name" :value="String(p.preType)" />
            </el-select>
          </el-form-item>
          <el-form-item label="格式">
            <el-select v-model="uploadForm.format" placeholder="自动识别" style="width:240px" clearable>
              <el-option v-for="(desc, code) in FORMAT_DESC" :key="code" :label="desc" :value="code" />
            </el-select>
          </el-form-item>
          <el-form-item>
            <input ref="fileInput" type="file" accept=".txt,.csv,.dat" style="display:none" @change="onFilePicked" />
            <el-button @click="$refs.fileInput.click()">选择文件（txt/csv/dat，≤2MB）</el-button>
            <span v-if="uploadForm.fileName" class="picked-name">{{ uploadForm.fileName }}</span>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" :loading="uploadLoading" @click="doUpload">上传</el-button>
          </el-form-item>
        </el-form>
      </div>
      <el-table v-loading="fileLoading" :data="myFiles" border empty-text="暂无上传文件" row-key="id"
        :header-cell-style="{ textAlign: 'center' }" :cell-style="{ textAlign: 'center' }"
        @expand-change="onExpandChange">
        <el-table-column type="expand">
          <template slot-scope="{ row }">
            <div class="file-detail" v-loading="row._loading">
              <template v-if="row._detail">
                <div class="detail-head">
                  <span>解析明细（共 {{ row._detail.hasPeriodRows ? row._detail.rows.length : row._detail.rawLines.length }} 条）</span>
                  <el-tag size="mini" :type="row._detail.hasPeriodRows ? 'success' : 'info'">
                    {{ row._detail.hasPeriodRows ? '已识别期号' : '无期号格式' }}
                  </el-tag>
                </div>
                <template v-if="row._detail.hasPeriodRows">
                  <el-table :data="row._detail.rows" size="mini" border max-height="320"
                    :header-cell-style="{ textAlign: 'center' }" :cell-style="{ textAlign: 'center' }">
                    <el-table-column type="index" label="序号" width="55" />
                    <el-table-column prop="period" label="期号" width="100" />
                    <el-table-column prop="openNbr" label="开奖号" min-width="200" show-overflow-tooltip>
                      <template slot-scope="s">{{ s.row.openNbr || '—' }}</template>
                    </el-table-column>
                    <el-table-column prop="saleMoney" label="销售额(元)" width="120">
                      <template slot-scope="s">{{ s.row.saleMoney || '—' }}</template>
                    </el-table-column>
                  </el-table>
                </template>
                <template v-else>
                  <pre class="raw-preview">{{ row._detail.rawLines.join('\n') }}</pre>
                </template>
              </template>
              <div v-else class="detail-empty">展开后自动加载解析明细</div>
            </div>
          </template>
        </el-table-column>
        <el-table-column type="index" label="序号" width="60" />
        <el-table-column prop="fileName" label="文件名" show-overflow-tooltip />
        <el-table-column prop="preName" label="彩种" width="100" />
        <el-table-column prop="formatDesc" label="识别格式" show-overflow-tooltip />
        <el-table-column prop="lineCount" label="数据行数" width="90" />
        <el-table-column prop="lastPeriod" label="最新期" width="100">
          <template slot-scope="{ row }">{{ row.lastPeriod || '按行数定位' }}</template>
        </el-table-column>
        <el-table-column label="上传时间" width="160">
          <template slot-scope="{ row }">{{ fmtTime(row.createTime) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="200">
          <template slot-scope="{ row }">
            <el-button type="primary" size="mini" :loading="fileGenLoadingId === row.id" @click="generateFromFile(row)">重新生成</el-button>
            <el-button type="danger" size="mini" @click="removeFile(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 我的更新日志 -->
    <el-card class="block" shadow="never">
      <div slot="header" class="card-head"><span>更新记录（生成后在此下载处理好的 txt）</span></div>
      <el-table v-loading="logLoading" :data="logList" border empty-text="暂无更新记录"
        :header-cell-style="{ textAlign: 'center' }" :cell-style="{ textAlign: 'center' }">
        <el-table-column type="index" label="序号" width="60" />
        <el-table-column label="彩种" width="100">
          <template slot-scope="{ row }">{{ lotteryTypeName(row.preType) }}</template>
        </el-table-column>
        <el-table-column prop="formatDesc" label="格式" show-overflow-tooltip />
        <el-table-column label="分区/版本" width="140">
          <template slot-scope="{ row }">{{ zonePhaseText(row) }}</template>
        </el-table-column>
        <el-table-column prop="periodFrom" label="起始期" width="100" />
        <el-table-column prop="periodTo" label="结束期" width="100" />
        <el-table-column prop="recordCount" label="期数" width="80" />
        <el-table-column label="结果" width="90">
          <template slot-scope="{ row }">
            <el-tag :type="row.status === 1 ? 'success' : 'danger'" size="small">
              {{ row.status === 1 ? '成功' : '失败' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="时间" width="160">
          <template slot-scope="{ row }">{{ fmtTime(row.createTime) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="100">
          <template slot-scope="{ row }">
            <el-button type="success" size="mini" :disabled="row.status !== 1"
              :loading="logDownloadingId === row.id" @click="downloadLog(row)">下载</el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        v-if="logTotal > 0"
        class="log-page"
        background
        layout="prev, pager, next"
        :total="logTotal"
        :page-size="logQuery.pageSize"
        :current-page.sync="logQuery.pageNum"
        @current-change="loadLogs"
      />
    </el-card>

    <!-- 购买弹窗：会员（月/年）或单次转换 -->
    <el-dialog :title="order ? '订单支付' : '选择套餐'" :visible.sync="payDialog" width="460px" append-to-body @closed="onPayClosed">
      <div v-if="order" class="pay-box">
        <div class="pay-row"><span>订单号：</span><b>{{ order.orderNo }}</b></div>
        <div class="pay-row"><span>金额：</span><b style="color:#f56c6c">{{ order.money }} 元</b></div>
        <div class="pay-row"><span>支付方式：</span><b>{{ payTypeText(order.payType) }}</b></div>

        <div v-if="order.payType === 'wxpay'" class="qr-box">
          <img v-if="order.codeUrl" :src="order.codeUrl" alt="微信扫码支付" class="qr-img" />
          <div v-else class="qr-tip">请使用微信扫描下方二维码完成支付</div>
        </div>

        <div v-else-if="order.payType === 'alipay'" ref="alipayBox" class="ali-box" v-html="order.htmlStr"></div>

        <div v-else class="qr-tip">
          余额支付订单已创建，请前往「个人中心 - 我的订单」完成支付，支付成功后点击下方按钮刷新。
        </div>
      </div>
      <div v-else class="pay-form">
        <div v-if="paywallMsg" style="color:#f56c6c;font-size:12px;line-height:1.7;background:#fef0f0;border-radius:6px;padding:10px 12px;margin-bottom:14px;">{{ paywallMsg }}</div>
        <div
          v-for="p in planOptions"
          :key="p.key"
          :style="planStyle(p.key)"
          @click="subscribeForm.buyType = p.key"
        >
          <div>
            <div style="font-size:14px;color:#303133;font-weight:500;">{{ p.name }}</div>
            <div style="font-size:12px;color:#909399;margin-top:3px;">{{ p.tip }}</div>
          </div>
          <div style="text-align:right;">
            <span style="color:#f56c6c;font-size:22px;font-weight:700;">¥{{ p.price }}</span>
            <span style="color:#909399;font-size:12px;">/{{ p.unit }}</span>
            <span v-if="subscribeForm.buyType === p.key" style="color:#409eff;font-size:12px;margin-left:6px;">✓</span>
          </div>
        </div>
        <div style="display:flex;align-items:center;justify-content:center;margin-top:18px;">
          <span style="font-size:13px;color:#606266;margin-right:12px;">支付方式</span>
          <el-radio-group v-model="subscribeForm.payType">
            <el-radio label="balancepay">余额</el-radio>
            <el-radio label="wxpay">微信</el-radio>
            <el-radio label="alipay">支付宝</el-radio>
          </el-radio-group>
        </div>
        <div style="color:#909399;font-size:12px;line-height:1.7;margin-top:12px;text-align:center;">
          免费版：每月 {{ subInfo && subInfo.fileMonthLimit !== undefined ? subInfo.fileMonthLimit : 3 }} 次、单次 ≤{{ priceInfo ? priceInfo.fileFreeBatchMax : 100 }} 期；会员不限次、不限期数
        </div>
        <div v-if="disclaimerText" style="color:#c0c4cc;font-size:11px;line-height:1.6;text-align:center;margin-top:8px;">{{ disclaimerText }}</div>
      </div>
      <div slot="footer">
        <template v-if="order">
          <el-button @click="payDialog = false">关闭</el-button>
          <el-button type="primary" @click="refreshAfterPay">我已支付，刷新状态</el-button>
        </template>
        <template v-else>
          <el-button @click="payDialog = false">取消</el-button>
          <el-button type="primary" :loading="payLoading" @click="doSubscribe">立即支付</el-button>
        </template>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getProducts, getSubscription, getLogList, subscribe, buySingle, getPrice, uploadMyFile, getMyFiles, deleteMyFile, getMyFileDetail, generateData, downloadLog } from "@/api/dataUpdate";

// 后端 generate 接口的请求体字段
// { preType, format, zone, phase, periodFrom, periodTo, fileId }

// 付费墙业务码：后端超免费额度/超单次期数时抛 ServiceException(code=4801)
const PAYWALL_CODE = 4801;

const LOTTERY_MAP = {
  '0': '双色球', '1': '大乐透', '2': '福彩3D', '3': '排列3',
  '5': '七乐彩', '6': '7星彩', '7': '排列5', '8': '快乐8',
};

// 后端 formatCodes 返回的是字符串数组（DataUpdateFormatEnum.code），描述在前端维护
const FORMAT_DESC_MAP = {
  DYJ_1: '期号 红球+蓝球（大赢家格式1）',
  DYJ_2: '仅开奖号码（大赢家格式2）',
  DYJ_3: '仅红球/仅前区（大赢家格式3）',
  DYJ_4: '期号+开奖号（大赢家格式4）',
  DYJ_5: '期数/日期/开奖号/销售额/一二等 CSV（大赢家格式5）',
  COLON: '期号:前区+后区（定制软件格式）',
  XY500: '幸运500样式（5位期号，竖线分区）',
};

export default {
  name: "DataUpdate",
  data() {
    return {
      products: [],
      subInfo: null,
      subLoading: false,
      logList: [],
      logTotal: 0,
      logLoading: false,
      logDownloadingId: null,
      logQuery: { pageNum: 1, pageSize: 20 },
      myFiles: [],
      fileLoading: false,
      fileGenLoadingId: null,
      uploadForm: { preType: '', format: '', fileName: '', file: null },
      uploadLoading: false,
      payDialog: false,
      payLoading: false,
      order: null,
      priceInfo: null,
      paywallMsg: '',
      subscribeForm: { buyType: 'month', payType: 'balancepay' },
    };
  },
  computed: {
    FORMAT_DESC() {
      return FORMAT_DESC_MAP;
    },
    disclaimerText() {
      return (this.priceInfo && this.priceInfo.disclaimer) ||
        '历史开奖数据为公开统计数据，仅用于历史走势查看，不构成购彩建议；本服务为数据格式化技术服务，不提供荐号/预测，不承诺提高中奖概率。';
    },
    planOptions() {
      const p = this.priceInfo || {};
      return [
        { key: 'single', name: '单次转换', tip: '转换一次，不限期数', price: this.fmtPrice(p.singlePrice, 2), unit: '次' },
        { key: 'month', name: '会员月付', tip: '一个月内不限次转换', price: this.fmtPrice(p.monthPrice, 9.9), unit: '月' },
        { key: 'year', name: '会员年付', tip: '一年内不限次转换，长期更划算', price: this.fmtPrice(p.yearPrice, 68), unit: '年' },
      ];
    },
  },
  mounted() {
    this.loadProducts();
    this.loadSubscription();
    this.loadPrice();
    this.loadLogs();
    this.loadMyFiles();
  },
  methods: {
    goBack() {
      this.$router.push('/home');
    },
    lotteryTypeName(type) {
      return LOTTERY_MAP[String(type)] || type || '-';
    },
    fmtDate(v) {
      if (!v) return '-';
      try { return new Date(v).toLocaleString('zh-CN'); } catch (e) { return v; }
    },
    fmtTime(v) {
      if (!v) return '-';
      const d = new Date(v);
      if (isNaN(d.getTime())) return v;
      const p = n => (n < 10 ? '0' + n : '' + n);
      return d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate()) + ' ' + p(d.getHours()) + ':' + p(d.getMinutes()) + ':' + p(d.getSeconds());
    },
    zonePhaseText(row) {
      const z = row.zone === 'FRONT' ? '前区' : (row.zone === 'BACK' ? '后区' : '全部');
      const p = row.phase === 'BEFORE' ? '试前' : '试后';
      return z + ' / ' + p;
    },
    payTypeText(t) {
      return t === 'wxpay' ? '微信' : (t === 'alipay' ? '支付宝' : '余额');
    },
    fmtPrice(v, fallback) {
      const n = Number(v);
      return isNaN(n) ? fallback : n;
    },
    loadPrice() {
      getPrice().then(res => {
        const data = res && res.data !== undefined ? res.data : res;
        this.priceInfo = data || null;
      }).catch(() => { this.priceInfo = null; });
    },
    loadProducts() {
      getProducts().then(res => {
        const data = res && res.data !== undefined ? res.data : res;
        this.products = Array.isArray(data) ? data : (data && data.rows) || [];
        if (!this.uploadForm.preType && this.products.length) {
          this.uploadForm.preType = String(this.products[0].preType);
        }
      }).catch(() => { this.products = []; });
    },
    loadSubscription() {
      this.subLoading = true;
      getSubscription().then(res => {
        const data = res && res.data !== undefined ? res.data : res;
        this.subInfo = data || null;
      }).catch(() => { this.subInfo = null; })
        .finally(() => { this.subLoading = false; });
    },
    loadLogs(page) {
      if (page) this.logQuery.pageNum = page;
      this.logLoading = true;
      getLogList(this.logQuery).then(res => {
        const data = res && res.data !== undefined ? res.data : res;
        if (Array.isArray(data)) {
          this.logList = data;
          this.logTotal = data.length;
        } else if (data && data.rows) {
          this.logList = data.rows;
          this.logTotal = data.total || data.rows.length;
        } else {
          this.logList = [];
          this.logTotal = 0;
        }
      }).catch(() => { this.logList = []; this.logTotal = 0; })
        .finally(() => { this.logLoading = false; });
    },
    loadMyFiles() {
      this.fileLoading = true;
      getMyFiles().then(res => {
        const data = res && res.data !== undefined ? res.data : res;
        this.myFiles = Array.isArray(data) ? data : (data && data.rows) || [];
      }).catch(() => { this.myFiles = []; })
        .finally(() => { this.fileLoading = false; });
    },
    onExpandChange(row, expandedRows) {
      // 仅在展开且尚未加载时拉取解析明细
      if (expandedRows.indexOf(row) === -1) return;
      if (row._detail || row._loading) return;
      this.$set(row, '_loading', true);
      getMyFileDetail(row.id).then(res => {
        const data = res && res.data !== undefined ? res.data : res;
        // 兜底：接口异常时 data 可能不是预期结构，必须保证 rows/rawLines 是数组
        const d = (data && typeof data === 'object' && Array.isArray(data.rows) && Array.isArray(data.rawLines))
          ? data : { hasPeriodRows: false, rows: [], rawLines: [] };
        this.$set(row, '_detail', d);
      }).catch(() => {
        this.$set(row, '_detail', { hasPeriodRows: false, rows: [], rawLines: [] });
        this.$message.error('加载解析明细失败');
      }).finally(() => { this.$set(row, '_loading', false); });
    },
    onFilePicked(e) {
      const files = e.target.files || [];
      this.uploadForm.file = files[0] || null;
      this.uploadForm.fileName = this.uploadForm.file ? this.uploadForm.file.name : '';
    },
    doUpload() {
      if (!this.uploadForm.preType) {
        this.$message.warning('请先选择彩种');
        return;
      }
      if (!this.uploadForm.file) {
        this.$message.warning('请先选择数据文件');
        return;
      }
      const fd = new FormData();
      fd.append('file', this.uploadForm.file);
      fd.append('preType', this.uploadForm.preType);
      if (this.uploadForm.format) {
        fd.append('format', this.uploadForm.format);
      }
      this.uploadLoading = true;
      uploadMyFile(fd).then(res => {
        if (res && res.code === PAYWALL_CODE) {
          // 付费墙：免费额度用尽/超期数上限，弹出购买引导
          this.$message.warning(res.msg || '免费额度已用完，购买单次转换或开通会员后可继续');
          this.openPay('single', (res && res.msg) || '');
          return;
        }
        this.$message.success('上传成功并已生成更新记录，请在下方「更新记录」中点击「下载」获取处理好的文件');
        this.uploadForm = { preType: this.uploadForm.preType, format: '', fileName: '', file: null };
        if (this.$refs.fileInput) this.$refs.fileInput.value = '';
        this.loadMyFiles();
        this.loadLogs();
        this.loadSubscription();
      }).catch(err => {
        if (err && err.code === PAYWALL_CODE) {
          this.openPay('single', (err && err.msg) || '');
          return;
        }
        this.$message.error('上传失败，请检查文件内容与格式');
      }).finally(() => { this.uploadLoading = false; });
    },
    generateFromFile(row) {
      // 仅"重新生成"一条更新记录（官方新开奖后刷新用），不立即下载
      this.fileGenLoadingId = row.id;
      generateData({ fileId: row.id }).then(res => {
        if (res && res.code === PAYWALL_CODE) {
          this.$message.warning(res.msg || '免费额度已用完，购买单次转换或开通会员后可继续');
          this.openPay('single', (res && res.msg) || '');
          return;
        }
        this.$message.success('已生成更新记录，请在下方「更新记录」中点击「下载」');
        this.loadLogs();
        this.loadSubscription();
      }).catch(err => {
        if (err && err.code === PAYWALL_CODE) {
          this.openPay('single', (err && err.msg) || '');
          return;
        }
        this.$message.error('生成更新记录失败，请稍后重试');
      }).finally(() => { this.fileGenLoadingId = null; });
    },
    downloadLog(row) {
      this.logDownloadingId = row.id;
      downloadLog(row.id).then(res => {
        // 拦截器把 HTTP 错误归一化为普通对象（非 Blob）；后端业务异常也会返回 JSON
        const blob = (res && res.data instanceof Blob) ? res.data : (res instanceof Blob ? res : null);
        if (!blob) {
          const msg = (res && res.msg) || (res && res.data && res.data.msg) || '下载失败';
          this.$message.error(msg);
          return;
        }
        if (blob.type && blob.type.indexOf('application/json') !== -1) {
          return blob.text().then(txt => {
            let msg = '下载失败';
            try { msg = (JSON.parse(txt) || {}).msg || msg; } catch (e) { /* 忽略 */ }
            this.$message.error(msg);
          });
        }
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'data_update_' + row.id + '_'
          + (row.fileName ? row.fileName.replace(/\.[^.]+$/, '') : row.preType) + '.txt';
        a.style.display = 'none';
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        this.$message.success('已开始下载处理好的开奖数据文件');
      }).catch(() => {
        this.$message.error('下载失败（可能登录已过期，请刷新后重试）');
      }).finally(() => { this.logDownloadingId = null; });
    },
    removeFile(row) {
      this.$confirm('确定删除「' + row.fileName + '」吗？', '提示', { type: 'warning' }).then(() => {
        deleteMyFile(row.id).then(() => {
          this.$message.success('已删除');
          this.loadMyFiles();
        }).catch(() => { this.$message.error('删除失败'); });
      }).catch(() => {});
    },
    planStyle(key) {
      const active = this.subscribeForm.buyType === key;
      return {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 16px', marginBottom: '10px', cursor: 'pointer',
        border: '1px solid ' + (active ? '#409eff' : '#ebeef5'),
        background: active ? '#ecf5ff' : '#ffffff',
        borderRadius: '8px', transition: 'all .2s'
      };
    },
    openPay(buyType, paywallMsg) {
      this.order = null;
      this.paywallMsg = paywallMsg || '';
      this.subscribeForm = { buyType: buyType || 'month', payType: 'balancepay' };
      this.payDialog = true;
    },
    doSubscribe() {
      if (!this.subscribeForm.buyType || !this.subscribeForm.payType) {
        this.$message.warning('请选择购买项与支付方式');
        return;
      }
      this.payLoading = true;
      const isSingle = this.subscribeForm.buyType === 'single';
      const req = isSingle
        ? buySingle({ payType: this.subscribeForm.payType })
        : subscribe({ plan: this.subscribeForm.buyType === 'year' ? 2 : 1, payType: this.subscribeForm.payType });
      req.then(res => {
        if (res && res.code !== undefined && res.code !== 200) {
          this.$message.error(res.msg || '下单失败，请稍后重试');
          return;
        }
        const data = res && res.data !== undefined ? res.data : res;
        this.order = data || {};
        this.$nextTick(() => {
          const box = this.$refs.alipayBox;
          const formEl = box && box.querySelector ? box.querySelector('form') : null;
          if (formEl) formEl.submit();
        });
      }).catch(() => { this.$message.error('下单失败，请稍后重试'); })
        .finally(() => { this.payLoading = false; });
    },
    refreshAfterPay() {
      this.loadSubscription();
      this.loadLogs();
      this.loadMyFiles();
      this.payDialog = false;
      this.$message.success('已刷新订阅状态');
    },
    onPayClosed() {
      this.order = null;
    },
  },
};
</script>

<style lang="scss" scoped>
.data-update-page {
  background: #ffffff;
  padding: 20px;
  margin: 20px auto;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  border-radius: 8px;

  .page-bar { margin-bottom: 10px; }
  .back-btn {
    display: inline-block; cursor: pointer; color: #347caf; font-size: 14px;
    padding: 6px 14px; border: 1px solid #347caf; border-radius: 5px;
    &:hover { background-color: #347caf; color: #ffffff; }
  }
  .page-title {
    font-size: 22px; font-weight: bold; color: #347caf; text-align: center;
    padding: 10px 0 20px; border-bottom: 2px solid #347caf; margin-bottom: 20px;
  }
  .tip-line {
    background: #f4f7fb; color: #666; font-size: 13px; padding: 10px 14px;
    border-radius: 6px; margin-bottom: 16px;
  }
  .block { margin-bottom: 18px; }
  .card-head {
    display: flex; align-items: center; justify-content: space-between; font-weight: bold;
  }
  .sub-box { font-size: 14px; }
  .sub-item { margin-left: 16px; color: #555; }
  .log-page { text-align: center; margin-top: 12px; }
  .upload-box { margin-bottom: 14px; }
  .upload-tip {
    background: #f4f7fb; color: #666; font-size: 13px; padding: 10px 14px;
    border-radius: 6px; margin-bottom: 12px; line-height: 1.8;
  }
  .upload-form { margin-bottom: 0; }
  .picked-name { margin-left: 10px; color: #347caf; font-size: 13px; }
  .file-detail { padding: 6px 18px 12px; background: #fafcff; }
  .detail-head { display: flex; align-items: center; margin-bottom: 8px; color: #555; font-size: 13px; }
  .detail-head .el-tag { margin-left: 10px; }
  .raw-preview {
    margin: 0; padding: 10px 12px; background: #fff; border: 1px solid #e8eef5; border-radius: 6px;
    font-size: 12px; line-height: 1.7; color: #333; white-space: pre-wrap; word-break: break-all; max-height: 320px; overflow: auto;
  }
  .detail-empty { color: #999; font-size: 13px; padding: 8px 0; }
  .pay-box { font-size: 14px; }
  .pay-row { margin-bottom: 8px; }
  .qr-box { text-align: center; margin-top: 10px; }
  .qr-img { width: 200px; height: 200px; }
  .qr-tip { color: #888; font-size: 13px; text-align: center; padding: 20px 0; }
  .ali-box { min-height: 40px; }
  .pay-form { padding: 0 0 4px; }
}
</style>
